import type { App } from "@/hooks/useApps";
import { getConnectorLogoUrl } from "@/lib/connectorLogo";
import { cn } from "@/lib/utils";
import Link from "next/link";

interface PublicAppCardProps {
  app: App;
  href?: string;
}

export function PublicAppCard({ app, href }: PublicAppCardProps) {
  const logoUrl = app.logoUrl || getConnectorLogoUrl(app.slug);

  return (
    <Link
      href={href ?? `/integrations/app/${app.slug}`}
      prefetch={false}
      className="rounded-ds-xl border-ds-border-default bg-ds-surface-primary shadow-ds-soft hover:border-ds-border-subtle sm:rounded-ds-22 lg:rounded-ds-2xl group relative flex aspect-square w-full items-center justify-center border p-2.5 transition-all sm:p-3"
    >
      <div className="rounded-ds-sm border-ds-border-default bg-ds-surface-card shadow-ds-b3-card-subtle sm:rounded-ds-md relative flex size-full flex-col items-center justify-center gap-3 overflow-hidden border px-3 py-4 transition-shadow group-hover:shadow-[0px_8px_16px_0px_rgba(17,23,46,0.06),0px_3px_4px_0px_rgba(17,23,46,0.03)] sm:gap-4 sm:px-4 sm:py-5 dark:group-hover:shadow-[0px_8px_16px_0px_rgba(0,0,0,0.3),0px_3px_4px_0px_rgba(0,0,0,0.2)]">
        <img
          src={logoUrl}
          alt={app.name}
          width={73}
          height={73}
          loading="lazy"
          decoding="async"
          className="rounded-ds-sm max-h-[40%] w-auto max-w-[60%] object-contain"
        />
        <p className="text-ds-text-subtle text-2-5xs line-clamp-2 text-center font-mono uppercase leading-tight sm:text-xs lg:text-sm">
          {app.name}
        </p>

        <CornerDot className="left-[10px] top-[10px]" />
        <CornerDot className="right-[10px] top-[10px]" />
        <CornerDot className="bottom-[10px] left-[10px]" />
        <CornerDot className="bottom-[10px] right-[10px]" />
      </div>
    </Link>
  );
}

function CornerDot({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn("bg-ds-border-default pointer-events-none absolute h-[6px] w-[6px] rounded-full", className)}
    />
  );
}
